import { getAuthToken, removeAuthToken } from './authToken'
import { API_CONFIG } from '../../api/config/api'

// Event emitter for 401 Unauthorized responses
type TokenExpiredListener = () => void
export let tokenExpiredListeners: TokenExpiredListener[] = []

/**
 * Subscribe to token expiration events
 * Used by React components to trigger re-authentication flow
 */
export const onTokenExpired = (
  listener: TokenExpiredListener,
): (() => void) => {
  tokenExpiredListeners.push(listener)
  // Return unsubscribe function
  return () => {
    tokenExpiredListeners = tokenExpiredListeners.filter(l => l !== listener)
  }
}

/**
 * Notify all listeners that token has expired
 * @internal
 */
const notifyTokenExpired = () => {
  console.warn(
    'Authentication: Token expired (401 Unauthorized), clearing token',
  )
  removeAuthToken()
  tokenExpiredListeners.forEach(listener => listener())
}

export interface ApiClientOptions extends RequestInit {
  /**
   * Whether to include Authorization header with the request.
   *
   * Authentication behavior by endpoint type:
   *
   * 1. PUBLIC ENDPOINTS (work without auth, but accept optional auth for personalization):
   *    - /boxes - Returns all boxes (optional bearer for user_vote_summary)
   *    - /boxes/{id} - Returns box details (optional bearer for user_vote_summary)
   *    - /faq - Returns FAQ items
   *    - /box-collections/* - Box collections
   *    → Use default includeAuth: true to get personalized data when logged in
   *
   * 2. PROTECTED ENDPOINTS (require JWT-auth - enforced by server):
   *    - /users/profile - User profile with TVL
   *    - /users/activity/votes - User vote history
   *    - /users/activity/created-boxes - Boxes created by user
   *    - /users/transaction-sent - Track sent transactions
   *    - /storage/upload - File upload
   *    - /bonuses/* - Bonus stats, tasks, referrals, claim
   *    → Must have auth token, server returns 401 if missing
   *
   * 3. AUTH ENDPOINTS (must not include auth):
   *    - /auth/generate-payload - Gets auth challenge
   *    - /auth/check-proof - Exchanges proof for token
   *    → Set includeAuth: false to prevent token conflicts
   *
   * @default true - Always includes auth token if available
   */
  includeAuth?: boolean
}

/**
 * Enhanced fetch wrapper that automatically adds Authorization header
 * if JWT token is available. This is a low-level function that doesn't
 * add baseUrl automatically - use apiGet/apiPost/etc for API calls.
 */
export const apiClient = async (
  url: string,
  options: ApiClientOptions = {},
): Promise<Response> => {
  const { includeAuth = true, headers, ...restOptions } = options

  // Merge headers - convert headers to a plain object
  const headersObject = headers
    ? Object.fromEntries(
        headers instanceof Headers
          ? Array.from(headers.entries())
          : Array.isArray(headers)
            ? headers
            : Object.entries(headers),
      )
    : {}

  const isFormData = restOptions.body instanceof FormData
  const hasCustomContentType = 'Content-Type' in headersObject

  const mergedHeaders: Record<string, string> = {
    // FormData needs browser to set Content-Type with boundary automatically
    ...(!isFormData &&
      !hasCustomContentType && { 'Content-Type': 'application/json' }),
    ...headersObject,
  }

  // Add Authorization header if includeAuth is true and token is available
  if (includeAuth) {
    const token = getAuthToken()
    if (token) {
      mergedHeaders['Authorization'] = `Bearer ${token}`
    }
  }

  const response = await fetch(url, {
    ...restOptions,
    headers: mergedHeaders,
  })

  // Check if token expired (401 Unauthorized)
  if (response.status === 401 && includeAuth) {
    notifyTokenExpired()
  }

  return response
}

/**
 * Convenience method for GET requests to our API
 * Automatically prepends API_CONFIG.baseUrl to the endpoint
 * For public endpoints (/boxes, /faq) - auth is optional
 * For protected endpoints - auth is required (but only enforced server-side)
 */
export const apiGet = (
  endpoint: string,
  options: ApiClientOptions = {},
): Promise<Response> => {
  const url = `${API_CONFIG.baseUrl}${endpoint}`

  // If includeAuth is not explicitly set, use default behavior:
  // Always try to include auth (if available) for all endpoints
  // This allows public endpoints to receive personalized data when user is logged in
  const finalOptions = {
    ...options,
    method: 'GET',
  }

  return apiClient(url, finalOptions)
}

/**
 * Convenience method for POST requests to our API
 * Automatically prepends API_CONFIG.baseUrl to the endpoint
 */
export const apiPost = (
  endpoint: string,
  body?: unknown,
  options: ApiClientOptions = {},
): Promise<Response> => {
  const url = `${API_CONFIG.baseUrl}${endpoint}`
  // Don't JSON.stringify FormData or other special body types
  const processedBody =
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob
      ? body
      : body
        ? JSON.stringify(body)
        : undefined

  return apiClient(url, {
    ...options,
    method: 'POST',
    body: processedBody,
  })
}

/**
 * Convenience method for PUT requests to our API
 * Automatically prepends API_CONFIG.baseUrl to the endpoint
 */
export const apiPut = (
  endpoint: string,
  body?: unknown,
  options: ApiClientOptions = {},
): Promise<Response> => {
  const url = `${API_CONFIG.baseUrl}${endpoint}`
  // Don't JSON.stringify FormData or other special body types
  const processedBody =
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob
      ? body
      : body
        ? JSON.stringify(body)
        : undefined

  return apiClient(url, {
    ...options,
    method: 'PUT',
    body: processedBody,
  })
}

/**
 * Convenience method for DELETE requests to our API
 * Automatically prepends API_CONFIG.baseUrl to the endpoint
 */
export const apiDelete = (
  endpoint: string,
  options: ApiClientOptions = {},
): Promise<Response> => {
  const url = `${API_CONFIG.baseUrl}${endpoint}`
  return apiClient(url, { ...options, method: 'DELETE' })
}
